import { useCallback, useEffect, useState } from "react";
import {
  adminStatements, adminCreateStatement, adminUpdateStatement, adminDeleteStatement,
  adminSubmitStatement, adminPublishStatement, adminRejectStatement, adminStatementSignatures,
  getBishops,
} from "@/lib/api";
import { Trash2, Send, CheckCircle2, XCircle, Signature, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import ResourceList from "@/components/admin/ResourceList";
import ResourceDrawer from "@/components/admin/ResourceDrawer";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["Statement", "Bishop Letter", "Testimony"];
const EMPTY = {
  slug: "", type: "Statement", title: "", author: "Colorado Catholic Conference",
  issue_areas: [], published_at: new Date().toISOString().slice(0, 10), excerpt: "", body: "",
};

const WORKFLOW_STYLES = {
  draft: "bg-stone-100 text-stone-700",
  pending_bishop: "bg-amber-100 text-amber-900",
  approved: "bg-emerald-100 text-emerald-800",
  published: "bg-ccc-purple/10 text-ccc-purple",
  rejected: "bg-red-100 text-red-700 line-through",
};

function StatementRow({ s, onEdit, onDelete, onSubmit, onPublish, onReject, onSignatures, isAdmin }) {
  const workflow = s.workflow_status || "legacy";
  const badge = WORKFLOW_STYLES[workflow] || "bg-stone-100 text-stone-700";
  return (
    <div className="bg-white border border-stone-200 p-5 flex items-start justify-between gap-3" data-testid={`statement-row-${s.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-ccc-gold-dark">{s.type} &middot; {s.published_at}</span>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${badge}`} data-testid={`stmt-workflow-${s.id}`}>
            {workflow === "legacy" ? "legacy · visible" : workflow.replace("_", " ")}
          </span>
        </div>
        <div className="font-serif text-xl text-ccc-plum">{s.title}</div>
        <div className="text-sm text-stone-600 mt-1">By {s.author}</div>
        {workflow === "rejected" && s.reject_reason && (
          <div className="text-xs text-red-700 mt-2 border-l-2 border-red-300 pl-2 italic">
            Rejected: {s.reject_reason}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 shrink-0 items-end">
        <div className="flex gap-2">
          <button onClick={() => onEdit(s)} className="text-ccc-purple text-sm underline">Edit</button>
          <button onClick={() => onDelete(s.id)} className="text-red-600" aria-label="Delete" data-testid={`stmt-delete-${s.id}`}>
            <Trash2 size={14}/>
          </button>
        </div>
        <div className="flex gap-1 flex-wrap justify-end mt-1">
          {(workflow === "draft" || workflow === "legacy" || workflow === "rejected") && (
            <Button size="sm" variant="outline" onClick={() => onSubmit(s)} data-testid={`stmt-submit-${s.id}`}>
              <Send size={12} className="mr-1"/> Submit
            </Button>
          )}
          {workflow === "pending_bishop" && (
            <>
              <Button size="sm" variant="outline" onClick={() => onSignatures(s)} data-testid={`stmt-signatures-${s.id}`}>
                <Signature size={12} className="mr-1"/> Signatures
              </Button>
              <Button size="sm" variant="outline" onClick={() => onReject(s)} data-testid={`stmt-reject-${s.id}`}
                      className="text-red-600 hover:text-red-700">
                <XCircle size={12} className="mr-1"/> Reject
              </Button>
            </>
          )}
          {workflow === "approved" && isAdmin && (
            <Button size="sm" onClick={() => onPublish(s.id)} data-testid={`stmt-publish-${s.id}`}
                    className="bg-ccc-purple hover:bg-ccc-plum">
              <CheckCircle2 size={12} className="mr-1"/> Publish
            </Button>
          )}
          {workflow === "approved" && !isAdmin && (
            <span className="text-xs text-stone-500 italic">Admin needed to publish</span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatementForm({ editing, setEditing }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <input placeholder="Slug (url)" value={editing.slug} onChange={e => setEditing({...editing, slug: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <select value={editing.type} onChange={e => setEditing({...editing, type: e.target.value})} className="px-3 py-2 border border-stone-300">
        {TYPES.map(t => <option key={t}>{t}</option>)}
      </select>
      <input placeholder="Title" value={editing.title} onChange={e => setEditing({...editing, title: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <input placeholder="Author" value={editing.author} onChange={e => setEditing({...editing, author: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <input type="date" value={editing.published_at} onChange={e => setEditing({...editing, published_at: e.target.value})} className="px-3 py-2 border border-stone-300"/>
      <textarea placeholder="Excerpt" rows={2} value={editing.excerpt} onChange={e => setEditing({...editing, excerpt: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300"/>
      <textarea placeholder="Body" rows={12} value={editing.body} onChange={e => setEditing({...editing, body: e.target.value})} className="col-span-2 px-3 py-2 border border-stone-300 font-serif"/>
    </div>
  );
}

export default function StatementsAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [signatures, setSignatures] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const load = useCallback(() => adminStatements().then(setItems), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (items.find(i => i.id === editing.id)) {
        await adminUpdateStatement(editing.id, editing); toast.success("Updated");
      } else {
        await adminCreateStatement(editing); toast.success("Saved draft");
      }
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await adminDeleteStatement(id); load();
  };
  const publish = async (id) => {
    if (!window.confirm("Publish this statement? It becomes live on the public site.")) return;
    try { await adminPublishStatement(id); toast.success("Published"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };

  return (
    <ResourceList
      eyebrow="Content"
      title="Statements & Letters."
      testId="admin-statements"
      newLabel="New draft"
      newButtonTestId="new-statement"
      onNew={() => setEditing({ ...EMPTY })}
    >
      <div className="grid gap-3">
        {items.map(s => <StatementRow key={s.id} s={s}
          isAdmin={isAdmin}
          onEdit={setEditing}
          onDelete={remove}
          onSubmit={setSubmitting}
          onPublish={publish}
          onReject={setRejecting}
          onSignatures={setSignatures}
        />)}
      </div>

      <ResourceDrawer
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit statement" : "New statement (draft)"}
        size="lg"
        onSave={save}
      >
        {editing && <StatementForm editing={editing} setEditing={setEditing}/>}
      </ResourceDrawer>

      <SubmitForApprovalDialog
        statement={submitting}
        onClose={() => setSubmitting(null)}
        onSubmitted={() => { setSubmitting(null); load(); }}
      />

      <SignaturesDialog
        statement={signatures}
        onClose={() => setSignatures(null)}
      />

      <RejectDialog
        statement={rejecting}
        onClose={() => setRejecting(null)}
        onRejected={() => { setRejecting(null); load(); }}
      />
    </ResourceList>
  );
}

/* --- Sub-dialogs -------------------------------------------------------- */

function SubmitForApprovalDialog({ statement, onClose, onSubmitted }) {
  const [bishops, setBishops] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (statement) {
      getBishops().then(setBishops).catch(() => setBishops([]));
      setSelected([]); setMessage("");
    }
  }, [statement]);

  const submit = async () => {
    if (selected.length === 0) { toast.error("Choose at least one bishop"); return; }
    setBusy(true);
    try {
      const r = await adminSubmitStatement(statement.id, { required_signer_ids: selected, message });
      toast.success(`Submitted — ${r.sign_requests?.length || 0} sign links generated`);
      onSubmitted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!statement} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="submit-approval-dialog">
        <DialogHeader>
          <DialogTitle>Submit for bishop approval</DialogTitle>
          <DialogDescription>
            Choose which bishops must sign before this statement can be published.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {bishops.length === 0 && (
            <div className="text-sm text-stone-500">Loading bishops…</div>
          )}
          {bishops.map(b => (
            <label key={b.id} className="flex items-start gap-3 p-2 border border-stone-200 hover:bg-stone-50 cursor-pointer"
                   data-testid={`bishop-check-${b.id}`}>
              <input type="checkbox"
                     checked={selected.includes(b.id)}
                     onChange={(e) => {
                       setSelected(e.target.checked
                         ? [...selected, b.id]
                         : selected.filter(x => x !== b.id));
                     }}
                     className="mt-1"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ccc-plum">{b.name}</div>
                <div className="text-xs text-stone-500">{b.diocese} · {b.title || "Bishop"}</div>
              </div>
            </label>
          ))}
          <div>
            <Label htmlFor="approval-msg">Optional note to the bishops</Label>
            <Input id="approval-msg" value={message} onChange={(e) => setMessage(e.target.value)}
                   placeholder="e.g. Please review by Friday"
                   data-testid="approval-message-input"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="submit-approval-btn">
            {busy ? "Sending…" : `Send to ${selected.length || "…"} bishop${selected.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignaturesDialog({ statement, onClose }) {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);
  useEffect(() => {
    if (statement) {
      adminStatementSignatures(statement.id).then(setData).catch(() => setData(null));
    } else { setData(null); }
  }, [statement]);
  const copyLink = async (url, bid) => {
    try { await navigator.clipboard.writeText(url); setCopied(bid); setTimeout(() => setCopied(null), 2000); }
    catch { toast.error("Copy failed"); }
  };
  if (!statement) return null;
  return (
    <Dialog open={!!statement} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="signatures-dialog">
        <DialogHeader>
          <DialogTitle>Bishop signatures</DialogTitle>
          <DialogDescription>
            Copy a signed link for each bishop. Links do not expire while the statement is <em>pending_bishop</em>.
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="text-sm text-stone-500">Loading…</div> : (
          <div className="space-y-3">
            <div className="text-xs text-stone-500 uppercase tracking-wider">
              Status: {data.workflow_status.replace("_", " ")} ·
              Signed: {data.signatures.length}/{data.required.length}
            </div>
            {data.required.map(r => (
              <div key={r.bishop_id} className="border border-stone-200 p-3" data-testid={`sig-row-${r.bishop_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-ccc-plum">{r.bishop_name}</div>
                    <div className="text-xs text-stone-500">{r.diocese}</div>
                  </div>
                  {r.signed ? (
                    <span className="text-emerald-700 text-sm flex items-center gap-1" data-testid={`sig-signed-${r.bishop_id}`}>
                      <CheckCircle2 size={14}/> Signed
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => copyLink(r.sign_url, r.bishop_id)}
                            data-testid={`sig-copy-${r.bishop_id}`}>
                      {copied === r.bishop_id ? <><Check size={12} className="mr-1"/> Copied</> : <><Copy size={12} className="mr-1"/> Copy link</>}
                    </Button>
                  )}
                </div>
                {!r.signed && (
                  <div className="text-xs text-stone-500 font-mono mt-1 truncate" title={r.sign_url}>
                    {r.sign_url}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ statement, onClose, onRejected }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (statement) setReason(""); }, [statement]);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try { await adminRejectStatement(statement.id, reason.trim()); toast.success("Rejected"); onRejected(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(false); }
  };
  if (!statement) return null;
  return (
    <Dialog open={!!statement} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader>
          <DialogTitle>Reject statement</DialogTitle>
          <DialogDescription>
            The statement moves to <em>rejected</em>. Existing signatures are kept for the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="reject-reason">Reason</Label>
          <Input id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. Bishop requested a revision"
                 data-testid="reject-reason-input"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="reject-confirm-btn">
            {busy ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
